<!-- (c) TwistedMinds, Gerry Uytdenhouwen - 4 november 2026 -->
<template>
    <div class="account-orders-v2" :style="rootStyle">
        <div class="account-orders-v2__title">{{ $t('general.my-orders') }}</div>

        <div v-if="customer" class="account-orders-v2__me">
            <div class="account-orders-v2__avatar">{{ initials }}</div>
            <div class="account-orders-v2__me-text">
                <div class="account-orders-v2__name">{{ customer.name }}</div>
                <div class="account-orders-v2__contact">
                    <span v-if="customer.email" class="account-orders-v2__contact-item">
                        <svg class="account-orders-v2__contact-icon" viewBox="0 0 24 24" aria-hidden="true">
                            <path :d="icons.email"/>
                        </svg>{{ customer.email }}</span>
                    <span v-if="customer.phone" class="account-orders-v2__contact-item">
                        <svg class="account-orders-v2__contact-icon" viewBox="0 0 24 24" aria-hidden="true">
                            <path :d="icons.phone"/>
                        </svg>{{ customer.phone }}</span>
                </div>
            </div>
        </div>

        <div v-if="loaded && cards.length === 0" class="account-orders-v2__empty">
            {{ $t('general.no-orders-yet') }}
        </div>

        <div v-for="card in cards" :key="card.id" class="account-orders-v2__card">
            <div class="account-orders-v2__top">
                <span class="account-orders-v2__when">
                    <svg class="account-orders-v2__when-icon" viewBox="0 0 24 24" aria-hidden="true">
                        <path :d="card.icon"/>
                    </svg>{{ card.moment }}</span>
                <span :class="['account-orders-v2__pill', 'account-orders-v2__pill--wide', card.pillClass]">
                    {{ card.statusLabel }}
                </span>
                <span v-if="card.amount" class="account-orders-v2__amount">{{ card.amount }}</span>
            </div>
            <div class="account-orders-v2__meta-row">
                <span :class="['account-orders-v2__pill', 'account-orders-v2__pill--narrow', card.pillClass]">
                    {{ card.statusLabel }}
                </span>
                <span class="account-orders-v2__meta">{{ card.kindLabel }}<span v-if="card.orderedOn"
                    class="account-orders-v2__ordered-on"> · {{ card.orderedOn }}</span></span>
            </div>

            <div v-if="card.lines.length > 0" class="account-orders-v2__details">
                <div v-for="(line, index) in visibleLines(card)" :key="index" class="account-orders-v2__line">
                    {{ line }}<template v-if="isLastVisible(card, index) && card.lines.length > maxLines">
                        <button type="button" class="account-orders-v2__more" @click="toggle(card.id)">
                            {{ isExpanded(card.id) ? $t('general.show_less') : '… ' + $t('general.show_more') }}
                        </button>
                    </template>
                </div>
            </div>
            <div v-if="card.note" class="account-orders-v2__details">
                {{ $t('general.order-note') }}: {{ card.note }}
            </div>

            <div class="account-orders-v2__actions">
                <button type="button" class="account-orders-v2__reorder" @click="$emit('reorder', card.id)">
                    <svg class="account-orders-v2__reorder-icon" viewBox="0 0 24 24" aria-hidden="true">
                        <path :d="icons.reorder"/>
                    </svg>{{ $t('general.reorder') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import {getCustomerOrders} from "@/widget/api";
import {
    customerInitials,
    deliveryKind,
    formatOrderAmount,
    formatOrderedOn,
    formatOrderMoment,
    orderLineText,
    orderStatusKey,
} from "@/widget/accountOrders";
import {DETAILS_MAX_LINES, detailsLines} from "@/utils/ordersTableV2";

// Path data from Material Design Icons (@mdi/js 5.8.55, same version as @mdi/font).
// Inline SVG because the widget bundle does not ship the MDI icon font.
const MDI = {
    calendarClock: "M15,13H16.5V15.82L18.94,17.23L18.19,18.53L15,16.69V13M19,8H5V19H9.67C9.24,18.09 9,17.07 9,16A7,7 0 0,1 16,9C17.07,9 18.09,9.24 19,9.67V8M5,21C3.89,21 3,20.1 3,19V5C3,3.89 3.89,3 5,3H6V1H8V3H16V1H18V3H19A2,2 0 0,1 21,5V11.1C22.24,12.36 23,14.09 23,16A7,7 0 0,1 16,23C14.09,23 12.36,22.24 11.1,21H5M16,11.15A4.85,4.85 0 0,0 11.15,16C11.15,18.68 13.32,20.85 16,20.85A4.85,4.85 0 0,0 20.85,16C20.85,13.32 18.68,11.15 16,11.15Z",
    truckOutline: "M18 18.5C18.83 18.5 19.5 17.83 19.5 17C19.5 16.17 18.83 15.5 18 15.5C17.17 15.5 16.5 16.17 16.5 17C16.5 17.83 17.17 18.5 18 18.5M19.5 9.5H17V12H21.46L19.5 9.5M6 18.5C6.83 18.5 7.5 17.83 7.5 17C7.5 16.17 6.83 15.5 6 15.5C5.17 15.5 4.5 16.17 4.5 17C4.5 17.83 5.17 18.5 6 18.5M20 8L23 12V17H21C21 18.66 19.66 20 18 20C16.34 20 15 18.66 15 17H9C9 18.66 7.66 20 6 20C4.34 20 3 18.66 3 17H1V6C1 4.89 1.89 4 3 4H17V8H20M3 6V15H3.76C4.31 14.39 5.11 14 6 14C6.89 14 7.69 14.39 8.24 15H15V6H3Z",
    locker: "M8,2H16A2,2 0 0,1 18,4V20A2,2 0 0,1 16,22H8A2,2 0 0,1 6,20V4A2,2 0 0,1 8,2M8,4V20H16V4H8M10,13H12V17H10V13M10,6H14V7.5H10V6M10,9H14V10.5H10V9Z",
    cartArrowDown: "M10,0V4H8L12,8L16,4H14V0M1,2V4H3L6.6,11.59L5.25,14.04C5.09,14.32 5,14.65 5,15A2,2 0 0,0 7,17H19V15H7.42C7.29,15 7.17,14.89 7.17,14.75L7.2,14.63L8.1,13H15.55C16.3,13 16.96,12.59 17.3,11.97L21.16,4.96L19.42,4H19.41L18.31,6L15.55,11H8.53L8.4,10.73L6.16,6L5.21,4L4.27,2M7,18A2,2 0 0,0 5,20A2,2 0 0,0 7,22A2,2 0 0,0 9,20A2,2 0 0,0 7,18M17,18A2,2 0 0,0 15,20A2,2 0 0,0 17,22A2,2 0 0,0 19,20A2,2 0 0,0 17,18Z",
    emailOutline: "M22 6C22 4.9 21.1 4 20 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6M20 6L12 11L4 6H20M20 18H4V8L12 13L20 8V18Z",
    phoneOutline: "M20,15.5C18.8,15.5 17.5,15.3 16.4,14.9C16.3,14.9 16.2,14.9 16.1,14.9C15.8,14.9 15.6,15 15.4,15.2L13.2,17.4C10.4,15.9 8,13.6 6.6,10.8L8.8,8.6C9.1,8.3 9.2,7.9 9,7.6C8.7,6.5 8.5,5.2 8.5,4C8.5,3.5 8,3 7.5,3H4C3.5,3 3,3.5 3,4C3,13.4 10.6,21 20,21C20.5,21 21,20.5 21,20V16.5C21,16 20.5,15.5 20,15.5M5,5H6.5C6.6,5.9 6.8,6.8 7,7.6L5.8,8.8C5.4,7.6 5.1,6.3 5,5M19,19C17.7,18.9 16.4,18.6 15.2,18.2L16.4,17C17.2,17.2 18.1,17.4 19,17.4V19Z",
};

const KIND_ICON = {pickup: MDI.calendarClock, delivery: MDI.truckOutline, vending: MDI.locker};
const KIND_LABEL = {pickup: 'general.order-pickup', delivery: 'general.delivery', vending: 'general.order-vending'};
const PILL_CLASS = {open: 'is-open', paid: 'is-paid'};

export default {
    props: {
        customer: {type: Object, default: null},
        theme: {type: Object, default: () => ({})},
        locale: {type: String, default: 'nl'},
        // '1' catalogue (product lines + note), '2' free text (details are the lines)
        shopMode: {type: [String, Number], default: 1},
    },
    data: () => ({
        loaded: false,
        orders: [],
        expanded: {},
        maxLines: DETAILS_MAX_LINES,
        icons: {email: MDI.emailOutline, phone: MDI.phoneOutline, reorder: MDI.cartArrowDown},
    }),
    computed: {
        rootStyle() {
            const radius = this.theme && this.theme.roundedCorners;
            return {
                '--account-orders-primary': (this.theme && this.theme.color) || '#1f2a2e',
                '--account-orders-radius': radius ? '14px' : '0',
                '--account-orders-button-radius': radius ? '10px' : '0',
                fontFamily: (this.theme && this.theme.fontFamily) || 'inherit',
            };
        },
        initials() {
            return customerInitials(this.customer && this.customer.name);
        },
        cards() {
            const freeText = String(this.shopMode) === '2';
            return this.orders.map((order) => {
                const kind = deliveryKind(order.delivery_type);
                const status = orderStatusKey(order.status);
                const orderedOn = formatOrderedOn(order.created_at, this.locale);
                const details = String(order.details ?? '').trim();
                const moment = [formatOrderMoment(order.date, order.time, this.locale)];
                if (kind !== 'pickup') {
                    moment.push(this.$t(KIND_LABEL[kind]));
                }
                return {
                    id: order.id,
                    icon: KIND_ICON[kind],
                    moment: moment.filter((part) => part !== '').join(' · '),
                    statusLabel: this.$t('general.' + status),
                    pillClass: PILL_CLASS[status] || 'is-closed',
                    amount: formatOrderAmount(order.amount, this.locale),
                    kindLabel: this.$t(KIND_LABEL[kind]),
                    orderedOn: orderedOn ? this.$t('general.ordered-on', orderedOn) : '',
                    lines: freeText
                        ? detailsLines(order.details)
                        : (Array.isArray(order.lines) ? order.lines : []).map(orderLineText),
                    note: freeText ? '' : details,
                };
            });
        },
    },
    methods: {
        isExpanded(id) {
            return Boolean(this.expanded[id]);
        },
        toggle(id) {
            this.$set(this.expanded, id, !this.expanded[id]);
        },
        visibleLines(card) {
            return this.isExpanded(card.id) ? card.lines : card.lines.slice(0, this.maxLines);
        },
        isLastVisible(card, index) {
            return index === this.visibleLines(card).length - 1;
        },
    },
    mounted() {
        getCustomerOrders()
            .then((response) => {
                this.orders = Array.isArray(response) ? response : [];
            })
            .catch(() => {
                this.orders = [];
            })
            .finally(() => {
                this.loaded = true;
            });
    },
};
</script>

<style scoped lang="scss">
// Fixed light palette: nothing is inherited from the host page or the widget's text colour.
.account-orders-v2 {
    color-scheme: light;
    background: #fff;
    color: #1f2a2e;
    font-size: 14px;
    line-height: normal;
    text-align: left;

    &__title {
        font-size: 20px;
        font-weight: 700;
        margin-bottom: 12px;
        color: #1f2a2e;
    }

    &__me {
        display: flex;
        gap: 12px;
        align-items: center;
        background: #f4f5f6;
        border-radius: var(--account-orders-radius);
        padding: 12px 14px;
        margin-bottom: 16px;
        color: #1f2a2e;
    }

    &__avatar {
        width: 40px;
        height: 40px;
        flex: 0 0 40px;
        border-radius: 50%;
        background: var(--account-orders-primary);
        color: #fff;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 700;
    }

    &__me-text {
        min-width: 0;
    }

    &__name {
        font-weight: 700;
        color: #1f2a2e;
    }

    &__contact {
        display: flex;
        flex-wrap: wrap;
        column-gap: 16px;
        color: #6b7a80;
        font-size: 13px;
        line-height: 1.5;
        overflow-wrap: anywhere;
    }

    // Explicit colours and fills below: element selectors in a host stylesheet (div, span, svg path)
    // beat inheritance, so nothing in this list may rely on an inherited colour.
    &__contact-item {
        color: #6b7a80;
    }

    &__contact-icon {
        width: 14px;
        height: 14px;
        margin-right: 3px;
        vertical-align: -0.125em;
        fill: currentColor;
        color: #6b7a80;

        path {
            fill: currentColor;
        }
    }

    &__empty {
        color: #6b7a80;
    }

    &__card {
        background: #fff;
        color: #1f2a2e;
        border: 1px solid #e8ecee;
        border-radius: var(--account-orders-radius);
        padding: 14px;
        margin-bottom: 10px;
    }

    &__top {
        display: flex;
        align-items: center;
        gap: 8px;
        flex-wrap: wrap;
    }

    &__when {
        font-weight: 700;
        font-size: 15px;
        color: #1f2a2e;
    }

    &__when-icon {
        width: 1em;
        height: 1em;
        margin-right: 4px;
        vertical-align: -0.125em;
        fill: var(--account-orders-primary);

        path {
            fill: var(--account-orders-primary);
        }
    }

    &__pill {
        border-radius: 999px;
        padding: 2px 9px;
        font-size: 11px;
        font-weight: 700;
        white-space: nowrap;

        &.is-open {
            background: #e5eef9;
            color: #2b5fa8;
        }

        &.is-paid {
            background: #e2f3ec;
            color: #1f7a55;
        }

        &.is-closed {
            background: #f1f3f4;
            color: #8f9ba0;
        }
    }

    &__pill--narrow {
        display: none;
    }

    &__amount {
        margin-left: auto;
        font-weight: 700;
        font-variant-numeric: tabular-nums;
        color: #1f2a2e;
    }

    &__meta {
        color: #6b7a80;
        font-size: 12px;
    }

    &__ordered-on {
        color: #6b7a80;
    }

    &__meta-row {
        margin-top: 2px;
    }

    &__details {
        font-size: 13px;
        line-height: 1.5;
        margin-top: 8px;
        color: #1f2a2e;
        overflow-wrap: anywhere;
    }

    &__line {
        color: #1f2a2e;
    }

    &__more {
        background: none;
        border: 0;
        padding: 0;
        margin-left: 4px;
        cursor: pointer;
        font: inherit;
        color: var(--account-orders-primary);
        font-size: 12px;
        font-weight: 600;
        line-height: 1.5;
        min-height: 0;
        text-transform: none;
        letter-spacing: normal;
        box-shadow: none;
    }

    &__actions {
        display: flex;
        justify-content: flex-end;
        margin-top: 10px;
    }

    &__reorder {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        border-radius: var(--account-orders-button-radius);
        padding: 8px 14px;
        font-family: inherit;
        font-weight: 600;
        font-size: 13px;
        background: #fff;
        border: 1.5px solid var(--account-orders-primary);
        color: var(--account-orders-primary);
        cursor: pointer;
        line-height: normal;
        min-height: 0;
        text-transform: none;
        letter-spacing: normal;
        box-shadow: none;
    }

    &__reorder-icon {
        width: 1em;
        height: 1em;
        fill: currentColor;
        color: var(--account-orders-primary);

        path {
            fill: currentColor;
        }
    }

    // Phone: status on the second row, contact details and button stacked (sketch variant A, "Gsm")
    @media (max-width: 479px) {
        &__contact {
            flex-direction: column;
            column-gap: 0;
        }

        &__pill--wide {
            display: none;
        }

        &__pill--narrow {
            display: inline;
        }

        &__meta-row {
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            margin-top: 4px;
        }

        &__ordered-on {
            display: none;
        }

        &__reorder {
            width: 100%;
            justify-content: center;
        }
    }
}
</style>
